import { useNavigate, useParams } from "react-router-dom";
import { CircularProgress, IconButton } from "@mui/material";
import { toast } from "react-toastify";
import { useGetSampleById } from "@services/samples/getById";
import ListPreview from "@commons/Preview/ListPreview";
import { prepareSamplesView } from "@utils/translator/samples";
import Heading from "@commons/Typography/Heading";
import { useEffect, useState } from "react";
import { config } from "@/config";
import { useGetSampleAttachments } from "@services/samples/getAttachmentsById";
import Text from "@commons/Typography/Text";
import AttachmentPreview from "@commons/Preview/AttachmentPreview";
import Button from "@commons/Button";
import PageviewIcon from "@mui/icons-material/Pageview";
import { formatDate } from "@utils/formatters";
import { usePermissions } from "@hooks/usePermissions";
import ModalEditSample from "../components/modal/ModalEditSample";
import { usePage } from "@hooks/usePage";

function ViewSample() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const { can } = usePermissions();
  const { openModal, setDataModal } = usePage();

  const {
    data: attachments,
    isLoading: isLoadingAttachments,
    error: attachmentsError,
  } = useGetSampleAttachments(id ?? "", {
    page: currentPage,
    limit: config.attachmentsGallery.defaultPageSize,
  });

  const { data: sample, isLoading, error } = useGetSampleById(id ?? null);

  const processedSample = sample ? prepareSamplesView(sample) : null;

  const handleEdit = () => {
    setDataModal({ id });
    openModal("editSample");
  };

  useEffect(() => {
    if (error) {
      toast.error("Erro ao carregar os dados da amostra.");
      navigate("/samples");
    }
  }, [error, navigate]);

  useEffect(() => {
    if (!isLoading && !sample && !error) {
      toast.warning("Amostra não encontrada.");
      navigate("/samples");
    }
  }, [isLoading, sample, error, navigate]);

  const canUpdateSample = can("update", "samples");

  return (
    <section className="flex flex-col gap-2 items-start w-full bg-white justify-start p-4 sm:p-6 shadow-md rounded-2xl">
      <div className="mb-4 flex flex-col sm:flex-row flex-wrap sm:items-center sm:justify-between w-full gap-4">
        <Heading className="mb-2 sm:mb-0">Detalhes da Amostra</Heading>

        <nav className="flex flex-col xs:flex-row gap-2 lg:gap-4 w-full sm:w-auto">
          <Button customVariant="primary" onClick={() => navigate("/samples")}>
            <Text size="sm">Voltar</Text>
          </Button>

          {canUpdateSample && (
            <Button customVariant="muted" onClick={handleEdit}>
              <Text size="sm">Editar</Text>
            </Button>
          )}
        </nav>
      </div>

      <div className="w-full">
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <CircularProgress size={60} className="text-primary" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="flex flex-col gap-2 items-start w-full h-full justify-start p-4 rounded-2xl border border-gray-200">
              <Heading size="sm">Informações Gerais</Heading>
              <ListPreview.Root>
                <ListPreview.Row
                  label="Tipo de Amostra:"
                  value={processedSample?.type || "—"}
                />
                <ListPreview.Row
                  label="Destino:"
                  value={processedSample?.destination || "—"}
                />
                <ListPreview.Row
                  label="Data da Amostra:"
                  value={processedSample?.sampleDate || "—"}
                />
                <ListPreview.Row
                  label="Coletor:"
                  value={processedSample?.reporter.username || "—"}
                />

                <ListPreview.Row
                  label="Última Edição por:"
                  value={processedSample?.lastEditor || "—"}
                />
              </ListPreview.Root>
            </div>
            <div className="flex flex-col gap-2 items-start w-full h-full justify-start p-4 rounded-2xl border border-gray-200">
              <div className="relative w-full">
                <Heading size="sm">Associações</Heading>
                <IconButton
                  className="absolute -top-2 left-28"
                  onClick={() =>
                    navigate(
                      "/individuals/" + (processedSample?.individual?.id || ""),
                    )
                  }
                  disabled={!processedSample?.individual}
                >
                  <PageviewIcon
                    className={`${!processedSample?.individual ? "opacity-70" : "text-primary"}`}
                  />
                </IconButton>
              </div>
              <ListPreview.Root>
                {processedSample?.visit && (
                  <ListPreview.Row
                    label="Visita Associada:"
                    value={`${processedSample?.visit?.location.address} | ${formatDate(processedSample?.visit?.date)}`}
                  />
                )}

                {processedSample?.individual && (
                  <>
                    <ListPreview.Row
                      label="Indivíduo Associado:"
                      value={`${processedSample.individual.commonName}, ID: ${processedSample.individual.clinicalRecord}`}
                    />
                    <ListPreview.Row
                      label="Espécie:"
                      value={processedSample.individual.taxonomicGroup.species}
                    />
                  </>
                )}
                {(processedSample?.anonymousIndividualTaxonomicGroup ||
                  processedSample?.anonymousIndividualGender) && (
                  <>
                    <ListPreview.Row
                      label="Espécie:"
                      value={
                        processedSample?.anonymousIndividualTaxonomicGroup
                          ?.species || "—"
                      }
                    />
                    <ListPreview.Row
                      label="Gênero:"
                      value={processedSample?.anonymousIndividualGender || "—"}
                    />
                  </>
                )}
              </ListPreview.Root>
            </div>

            <div className="flex flex-col lg:col-span-2 gap-2 items-start w-full h-full justify-start p-4 rounded-2xl border border-gray-200 overflow-x-auto">
              <Heading size="sm">Anexos</Heading>

              {isLoadingAttachments && (
                <div className="flex justify-center items-center py-4">
                  <CircularProgress size={24} className="text-primary" />
                  <Text size="sm" className="ml-2 text-gray-500">
                    Carregando anexos...
                  </Text>
                </div>
              )}

              {attachmentsError && (
                <div className="flex items-center py-2">
                  <Text size="sm" className="text-red-500">
                    Erro ao carregar anexos. Tente novamente.
                  </Text>
                </div>
              )}

              {!isLoadingAttachments &&
                !attachmentsError &&
                attachments &&
                attachments?.items.length > 0 && (
                  <AttachmentPreview
                    attachments={attachments.items}
                    pagination={{
                      pageSize: config.attachmentsGallery.defaultPageSize,
                      currentPage: attachments.meta.currentPage,
                      totalPages: attachments.meta.totalPages,
                      totalItems: attachments.meta.totalItems,
                      onPageChange: setCurrentPage,
                    }}
                  />
                )}

              {!isLoadingAttachments &&
                !attachmentsError &&
                (!attachments || attachments?.items.length === 0) && (
                  <Text size="sm" className="text-gray-500 py-2">
                    Nenhum anexo encontrado.
                  </Text>
                )}
            </div>

            <div className="flex flex-col lg:col-span-2 gap-2 items-start w-full h-full justify-start p-4 rounded-2xl border border-gray-200">
              <Heading size="sm">Observações</Heading>
              {processedSample?.observations ? (
                <Text size="sm" className="text-gray-700 whitespace-pre-wrap">
                  {processedSample.observations}
                </Text>
              ) : (
                <Text size="sm" className="text-gray-500 py-2">
                  Nenhuma observação encontrada.
                </Text>
              )}
            </div>
          </div>
        )}
      </div>
      <ModalEditSample />
    </section>
  );
}

export default ViewSample;
